import { getCookie } from '@/features/shared/utils/cookies'
import { getItem } from '@/features/shared/utils/localStorage'
import { logError, logInfo } from '@/features/shared/utils/logger'
import { type ConsentPurposesAPI } from '@/lib/transcend/useConsentManager'
import { TRANSCEND_ALLOW_ALL_CONSENT, TRANSCEND_CONSENT_LS_KEY } from '@shared/constants/transcend'
import { isOnServer } from '@shared/constants/util'
import { type Consent } from '@shipt/analytics-core'

/**
 * In the event that Transcend is disabled or experiencing an outage, the TRANSCEND_ALLOW_ALL_CONSENT flag is used.
 * When set to 'active', all analytics will be permitted without requiring user consent.
 * This flag is currently set to 'blocked' because Transcend consent is enforced.
 */
const getIsAllConsentAllowed = (): boolean => {
  if (isOnServer()) {
    return process.env.TRANSCEND_ALLOW_ALL_CONSENT === 'active'
  }
  return getCookie(TRANSCEND_ALLOW_ALL_CONSENT) === 'active'
}

export const getTranscendConsentPurposes = (): ConsentPurposesAPI | undefined => {
  if (isOnServer()) {
    return undefined
  }
  if (window.airgap && 'getConsent' in window.airgap) {
    const purposes: Record<string, unknown> = window.airgap.getConsent().purposes
    return purposes
  }
  const storedConsent = getItem(TRANSCEND_CONSENT_LS_KEY)
  if (storedConsent) {
    try {
      const purposes: ConsentPurposesAPI = JSON.parse(storedConsent).purposes
      return purposes
    } catch (error) {
      logError('Failed to parse the stored transcend consent purposes from localStorage', {
        storedConsent,
        error,
      })
      return undefined
    }
  }
  return undefined
}

export const getConsentObject = (): Consent | undefined => {
  const consentPurposes = getTranscendConsentPurposes()
  if (consentPurposes) {
    return {
      categoryPreferences: {
        Analytics: Boolean(consentPurposes.Analytics),
        Advertising: Boolean(consentPurposes.Advertising),
        Functional: Boolean(consentPurposes.Functional),
      },
    }
  } else return undefined
}

export const getIsConsentedToAnalytics = () =>
  getIsAllConsentAllowed() || Boolean(getTranscendConsentPurposes()?.Analytics)

export const waitForAirgap = (
  callback: (consent: Consent | undefined) => void,
  logObject?: Record<string, unknown>,
  timeout = 20_000,
) => {
  const startTime = Date.now()
  const pollInterval = 1_000

  const poll = setInterval(() => {
    if (getTranscendConsentPurposes()) {
      clearInterval(poll)
      if (getIsConsentedToAnalytics()) {
        logInfo('Transcend airgap script loaded using waitForAirgap method', logObject)
        callback(getConsentObject())
      }
    } else if (Date.now() - startTime >= timeout) {
      clearInterval(poll)
      logInfo('Transcend airgap script not loaded within the defined timeout', logObject)
    }
  }, pollInterval)
}
